import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, isApiError, type DiagnosticGroupMember } from '../lib/api'

/** Restricted to this one account, both here and (for real) server-side in admin-diagnostics.ts —
 *  the client check only avoids flashing a control that would 403 for everyone else. */
const DIAGNOSTIC_ALLOWED_EMAIL = 'alban.alev@capgemini.com'

/**
 * Client-side gate for {@link GraphDiagnosticsSection} — modelled on `lib/beta.ts#useBetaTester`
 * (same `['me']` query, same "not yet resolved = hide" default). The real gate is server-side.
 */
export function useDiagnosticsAccess(): boolean {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })
  return me.data?.email === DIAGNOSTIC_ALLOWED_EMAIL
}

type Result =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; members: DiagnosticGroupMember[] }
  | { kind: 'error'; message: string }

/**
 * TEMPORARY diagnostic section — not a product feature, and not localized (English only, no `t()`
 * keys) on purpose: it's a single-account tool meant to be deleted, not translated. Answers one
 * question empirically for `plans/BID_SITE_MEMBER_SYNC.md`'s "still open" #2 — does this app's
 * already-consented delegated `Group.Read.All` actually cover `GET /groups/{id}/members`? Delete
 * this file, its wiring in AccountSettingsPage.tsx, and the `diagnosticGroupMembers` entry in
 * lib/api.ts once that's answered either way.
 */
export function GraphDiagnosticsSection() {
  const [groupId, setGroupId] = useState('')
  const [result, setResult] = useState<Result>({ kind: 'idle' })

  async function runTest(): Promise<void> {
    const id = groupId.trim()
    if (!id) return
    setResult({ kind: 'loading' })
    try {
      const res = await api.diagnosticGroupMembers(id)
      setResult({ kind: 'ok', members: res.members })
    } catch (e) {
      setResult({
        kind: 'error',
        message: isApiError(e) ? e.serverMessage || e.message : 'Request failed',
      })
    }
  }

  return (
    <div className="settings-card">
      <p>
        Entra group object id (the M365 group behind a Team-connected SharePoint site) — tests
        whether <code>GET /groups/&#123;id&#125;/members</code> works on this app's current Graph
        grant.
      </p>
      <div className="settings-card-head">
        <input
          type="text"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          placeholder="Entra group object id (GUID)"
          aria-label="Entra group object id"
        />
        <button type="button" onClick={() => void runTest()} disabled={result.kind === 'loading'}>
          Test
        </button>
      </div>
      {result.kind === 'loading' && <p className="settings-note">Checking…</p>}
      {result.kind === 'error' && (
        <p className="settings-note" role="alert">
          {result.message}
        </p>
      )}
      {result.kind === 'ok' && (
        <>
          <p className="settings-note">{result.members.length} member(s) returned.</p>
          <ul>
            {result.members.map((m) => (
              <li key={m.oid} className="mono">
                {m.displayName} — {m.upn ?? '(no upn)'} — {m.userType ?? '(no userType)'}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
